import { ExternalCalendarEventPreview, ID } from '@griply/types';
import {
  buildEventFormSeed,
  getExternalCalendarEventRef,
  getUnparseableBoundaryReport,
  parseEventBoundaryPair,
} from '@griply/utils';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useOpenEditEventPopup } from 'shared/contexts/event-form';
import { getErrorReporter } from 'shared/services/error-tracking';

export type ExternalEventPopupTarget = {
  externalId: ID;
  title: string;
  allDay: boolean;
  // a date key (yyyy-MM-dd) when allDay, an ISO instant otherwise; an all-day end is exclusive
  start?: string;
  end?: string;
  // absent on responses from an older backend — the caller falls back to the provider url
  connectionId?: ID;
  calendarId?: ID;
  // the event at the provider — the only place an invitee can counter-propose a time
  url?: string;
  preview?: ExternalCalendarEventPreview;
};

// Both surfaces that open an external event (the calendar card and the day-section banner
// row) need the same three things: the connection/calendar pair, the seed built from what
// is already on screen, and an anchor snapshotted from the element's rect — snapshotted
// rather than a live ref because FullCalendar re-creates those nodes on every data update.
export const useOpenExternalEventPopup = (
  containerRef: React.RefObject<HTMLElement | null>,
  target: ExternalEventPopupTarget,
) => {
  const openEditEvent = useOpenEditEventPopup();
  const {
    externalId,
    title,
    allDay,
    start,
    end,
    connectionId,
    calendarId,
    url,
    preview,
  } = target;

  const boundaries = useMemo(
    () => parseEventBoundaryPair(start, end, allDay),
    [allDay, end, start],
  );

  // A boundary the form cannot render degrades to the provider url, the same fallback an absent pair takes.
  const eventTarget = boundaries.isUnparseable
    ? null
    : getExternalCalendarEventRef({
        eventId: externalId,
        connectionId,
        calendarId,
      });

  const onOpen = useCallback(() => {
    if (!eventTarget || !boundaries.start) {
      return;
    }
    const rect = containerRef.current?.getBoundingClientRect();
    openEditEvent(eventTarget, {
      anchor: { top: rect?.top ?? 0, left: rect?.right ?? 0 },
      url,
      preview,
      seed: buildEventFormSeed({
        title,
        allDay,
        start: boundaries.start,
        end: boundaries.end,
        calendar: {
          connectionId: eventTarget.connectionId,
          calendarId: eventTarget.calendarId,
        },
        preview,
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- eventTarget is rebuilt each render from the primitives below
  }, [
    allDay,
    boundaries,
    calendarId,
    connectionId,
    containerRef,
    externalId,
    openEditEvent,
    preview,
    title,
    url,
  ]);

  useEffect(() => {
    if (!boundaries.isUnparseable) {
      return;
    }
    const { error, context } = getUnparseableBoundaryReport({
      allDay,
      start,
      end,
      eventId: externalId,
      connectionId,
      calendarId,
    });
    getErrorReporter().captureException(error, context);
  }, [
    allDay,
    boundaries.isUnparseable,
    calendarId,
    connectionId,
    end,
    externalId,
    start,
  ]);

  return { eventTarget, onOpen };
};
